// Favorite emoji: the ones starred in the picker (shared/src/favoriteEmoji.ts), kept on the account so every device
// and client shows the same ones. A star or unstar changes the list here at once and goes to the server as that one
// change (`favoriteEmojiAdd` / `favoriteEmojiRemove`), never as the whole list, so a tab or device with an old copy
// can't undo what another starred; the server answers with the list as it now stands. Changes go one at a time and
// a failed one is tried again. A copy in localStorage gives the picker its favorites before the account loads and
// keeps this browser's tabs in step.
import { useMemo, useSyncExternalStore } from "react";
import { applyFavoriteChange, emojiToken, isFavorite, keepFavoriteEmoji, MAX_FAVORITE_EMOJI, parseEmojiToken, type CustomEmoji, type FavoriteChange, type UserSettings } from "@bloom/shared";
import { api, ApiError } from "./api.ts";

const KEY = "bloom:favorite-emoji";
const listeners = new Set<() => void>();

function stored(): string[] {
  try {
    return keepFavoriteEmoji(JSON.parse(localStorage.getItem(KEY) ?? "[]"));
  } catch {
    return [];
  }
}

let list: string[] = stored();
/** Changes made here that the server hasn't taken yet, oldest first. */
const queue: FavoriteChange[] = [];
/** Counts changes made here, so a copy of the account read before one of them isn't taken over it. */
let made = 0;
let sending = false;
let retryIn = 0;

function show(next: string[], { keep = true } = {}) {
  list = next;
  if (keep) {
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // private mode: still applies for this page
    }
  }
  for (const fn of listeners) fn();
}

async function send() {
  if (sending) return;
  sending = true;
  while (queue.length) {
    const change = queue[0]!;
    try {
      const s = await api<UserSettings>("/me/settings", { method: "PUT", body: { favoriteEmojiAdd: change.add, favoriteEmojiRemove: change.remove } });
      queue.shift();
      retryIn = 0;
      // The server's list has everything starred anywhere; with nothing of mine still on its way, it's the one to show.
      if (!queue.length && Array.isArray(s?.favoriteEmoji)) show(keepFavoriteEmoji(s.favoriteEmoji));
    } catch (e) {
      // Refused (not signed in, or a favorite this server doesn't take): trying again won't help, so it's dropped and
      // the next account copy shows what's really stored.
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) {
        queue.shift();
        continue;
      }
      // Offline or refused for now: keep it and try again (2 s, doubling to a minute, or when back online).
      retryIn = Math.min(retryIn ? retryIn * 2 : 2000, 60_000);
      sending = false;
      setTimeout(() => void send(), retryIn);
      return;
    }
  }
  sending = false;
}

if (typeof addEventListener === "function") {
  addEventListener("online", () => void send());
  // Another tab of this browser starred or unstarred.
  addEventListener("storage", (e) => {
    if (e.key === KEY) show(stored(), { keep: false });
  });
}

/**
 * Stars or unstars one (a server emoji as its token). Returns false, changing nothing, when it would be a new favorite
 * and the list is full.
 */
export function toggleFavorite(emoji: string): boolean {
  const change: FavoriteChange = isFavorite(list, emoji) ? { remove: emoji } : { add: emoji };
  if (change.add && list.length >= MAX_FAVORITE_EMOJI) return false;
  made++;
  show(applyFavoriteChange(list, change));
  queue.push(change);
  void send();
  return true;
}

export const getFavoriteEmoji = () => list;
/** How many changes were made here so far: read before asking for the account, and pass it to takeAccountFavorites. */
export const favoriteChangesMade = () => made;

/**
 * The account's list (from /api/me or /api/me/settings), shown unless a change was made here since it was asked for
 * (`madeBefore`) or one is still on its way: then this device's list is newer.
 */
export function takeAccountFavorites(theirs: unknown, madeBefore: number) {
  if (!Array.isArray(theirs) || made !== madeBefore || queue.length) return;
  const next = keepFavoriteEmoji(theirs);
  if (JSON.stringify(next) !== JSON.stringify(list)) show(next);
}

let askedAt = -Infinity;

/**
 * Asks the account for its list (the picker does on opening), so favorites starred elsewhere show; at most every 30 s
 * (every picker open would otherwise be a read).
 */
export async function refreshFavorites() {
  if (Date.now() - askedAt < 30_000) return;
  askedAt = Date.now();
  const before = made;
  try {
    const s = await api<UserSettings>("/me/settings");
    takeAccountFavorites(s?.favoriteEmoji, before);
  } catch {
    // keep what's here
  }
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useFavoriteList = (): string[] => useSyncExternalStore(subscribe, () => list);

/**
 * The favorites that can be shown here: Unicode, plus this server's own emoji that still exist, as their current token
 * (a renamed one carries its new name). Another server's emoji, and deleted ones, stay on the list but don't show:
 * `hidden` counts them.
 */
export function favoritesHere(all: readonly string[], byId: Map<string, CustomEmoji>): { shown: string[]; hidden: number } {
  const shown = all.flatMap((u) => {
    const t = parseEmojiToken(u);
    if (!t) return [u];
    const e = byId.get(t.id);
    return e ? [emojiToken(e)] : [];
  });
  return { shown, hidden: all.length - shown.length };
}

export function useFavoritesHere(byId: Map<string, CustomEmoji>): { shown: string[]; hidden: number } {
  const all = useFavoriteList();
  return useMemo(() => favoritesHere(all, byId), [all, byId]);
}
